import React, { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { ThemeProvider } from "@emotion/react";
import store from "../../store";
import theme from "../../themes/theme";
import AuthorizationProvider from "../../common/providers/AuthorizationProvider";
import LaunchDarklyProvider from "../../common/launchDarkly/LaunchDarklyProvider";
import Sidebar, {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from "../ProjectLayout/Sidebar";

interface AppChromeLayoutProps {
  account_slug: string;
  account_id: string;
}

// Chrome-only shell — left Sidebar, no top-nav — for legacy server-rendered
// main-app pages (e.g. the analytics Dashboard) that need to render inside the
// new nav under release.react-nav but aren't React MSPAs. The Rails layout
// `app_chrome.html.haml` mounts this and `= yield`s the server HTML beside the
// sidebar, offset by the `--app-sidebar-width` CSS var this component publishes.
// The sidebar uses reloadDocument because this root carries no client-side
// content routes: every nav destination is a full-page GET.
export const AppChromeLayoutInner: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const sidebarWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-sidebar-width",
      `${sidebarWidth}px`
    );
  }, [sidebarWidth]);

  return (
    <Sidebar
      expanded={sidebarExpanded}
      onToggle={() => setSidebarExpanded((prev) => !prev)}
      reloadDocument
    />
  );
};

const AppChromeLayout: React.FC<AppChromeLayoutProps> = ({ account_slug }) => {
  return (
    <Provider store={store}>
      <AuthorizationProvider account_slug={account_slug}>
        <ThemeProvider theme={theme}>
          <LDProvider
            clientSideID={
              process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
            }
          >
            <LaunchDarklyProvider accountSlug={account_slug}>
              <BrowserRouter>
                <AppChromeLayoutInner />
              </BrowserRouter>
            </LaunchDarklyProvider>
          </LDProvider>
        </ThemeProvider>
      </AuthorizationProvider>
    </Provider>
  );
};

export default AppChromeLayout;
